/**
 * 高德行政区服务(AMap.DistrictSearch)—— 城市/区县联动的唯一数据源(P2 正式做法,2026-08-24 用户拍板)。
 *
 * 形状约定:
 * - 城市清单一次拉全国(subdistrict=2,extensions=base 只要中心点不拉边界,payload 可控),模块级缓存。
 * - 直辖市(北京/天津/上海/重庆)与港澳在省级节点上就是「城市」,拍平进城市清单(纯函数,有单测)。
 * - 边界(boundaries)按需单查所选 adcode(extensions=all 很重,不进全国查询)。
 * - 任何一步失败一律返回 null —— SearchPanel 退回旧交互(静态城市 + 区域手打),搜索链路不因此断。
 */
import { loadAmap } from "./amapLoader";

/* AMap 无官方 TS 类型,本文件收口 any,组件侧只见下面的具名形状。 */
/* eslint-disable @typescript-eslint/no-explicit-any */

export interface DistrictNode {
  name: string;
  adcode: string;
  center: { lng: number; lat: number } | null;
  /** province=省 city=市 district=区县(直辖市拍平后 level 仍标 city)。 */
  level: string;
}

export interface CityGroup {
  province: string;
  cities: DistrictNode[];
}

/** 直辖市/特区 adcode:省级节点即城市。 */
const MUNICIPALITY_ADCODES = new Set(["110000", "120000", "310000", "500000", "810000", "820000"]);

function toNode(raw: any, levelOverride?: string): DistrictNode {
  let center: DistrictNode["center"] = null;
  const c = raw?.center;
  if (c && typeof c.lng === "number" && typeof c.lat === "number") {
    center = { lng: c.lng, lat: c.lat };
  } else if (typeof c === "string" && c.includes(",")) {
    const [lng, lat] = c.split(",").map(Number);
    if (Number.isFinite(lng) && Number.isFinite(lat)) center = { lng, lat };
  }
  return {
    name: String(raw?.name ?? ""),
    adcode: String(raw?.adcode ?? ""),
    center,
    level: levelOverride ?? String(raw?.level ?? ""),
  };
}

/**
 * 全国省列表 → 分组城市清单(纯函数,单测靶)。
 * 直辖市:省节点自身作为城市入组(组名=自身);普通省:children 城市入组。
 */
export function buildCityGroups(provinces: any[]): CityGroup[] {
  const groups: CityGroup[] = [];
  for (const p of provinces ?? []) {
    const node = toNode(p);
    if (!node.name) continue;
    if (MUNICIPALITY_ADCODES.has(node.adcode)) {
      groups.push({ province: node.name, cities: [{ ...node, level: "city" }] });
      continue;
    }
    const cities = (p.districtList ?? [])
      .map((c: any) => toNode(c, "city"))
      .filter((c: DistrictNode) => c.name);
    if (cities.length > 0) {
      groups.push({ province: node.name, cities });
    }
  }
  return groups;
}

/**
 * 城市的区县列表(纯函数,单测靶)。
 * 直辖市查询返回「XX城区」中间层,区县在孙子层 —— 拍平;普通市区县就在子层。
 */
export function flattenDistricts(cityRaw: any): DistrictNode[] {
  const children: any[] = cityRaw?.districtList ?? [];
  const out: DistrictNode[] = [];
  for (const child of children) {
    const node = toNode(child);
    if (node.level === "district" || (child?.districtList ?? []).length === 0) {
      if (node.name) out.push({ ...node, level: "district" });
    } else {
      for (const g of child.districtList ?? []) {
        const gn = toNode(g, "district");
        if (gn.name) out.push(gn);
      }
    }
  }
  return out;
}

// ── AMap 调用层(模块级缓存;全部失败即 null) ──────────────────────────────

let cityGroupsCache: CityGroup[] | null | undefined;
const districtsCache = new Map<string, DistrictNode[] | null>();
const boundaryCache = new Map<string, { center: DistrictNode["center"]; paths: any[] } | null>();

function newSearcher(AMap: any, opts: Record<string, unknown>): Promise<any | null> {
  return new Promise((resolve) => {
    try {
      AMap.plugin("AMap.DistrictSearch", () => {
        try {
          resolve(new AMap.DistrictSearch(opts));
        } catch {
          resolve(null);
        }
      });
    } catch {
      resolve(null);
    }
  });
}

function searchOne(searcher: any, keyword: string): Promise<any | null> {
  return new Promise((resolve) => {
    try {
      searcher.search(keyword, (status: string, result: any) => {
        resolve(status === "complete" ? (result?.districtList?.[0] ?? null) : null);
      });
    } catch {
      resolve(null);
    }
  });
}

/** 全国城市清单(分组);失败 null → 调用方退回静态交互。 */
export async function listCityGroups(): Promise<CityGroup[] | null> {
  if (cityGroupsCache !== undefined) return cityGroupsCache;
  const AMap: any = await loadAmap();
  if (!AMap) return (cityGroupsCache = null);
  const searcher = await newSearcher(AMap, {
    level: "country",
    subdistrict: 2,
    extensions: "base",
  });
  if (!searcher) return (cityGroupsCache = null);
  const country = await searchOne(searcher, "中国");
  const groups = country ? buildCityGroups(country.districtList ?? []) : null;
  return (cityGroupsCache = groups && groups.length > 0 ? groups : null);
}

/** 某城市的区县列表(adcode 定位;直辖市自动拍平)。 */
export async function listDistricts(cityAdcode: string): Promise<DistrictNode[] | null> {
  const hit = districtsCache.get(cityAdcode);
  if (hit !== undefined) return hit;
  const AMap: any = await loadAmap();
  if (!AMap) return null;
  const isMunicipality = MUNICIPALITY_ADCODES.has(cityAdcode);
  const searcher = await newSearcher(AMap, {
    level: isMunicipality ? "province" : "city",
    subdistrict: isMunicipality ? 2 : 1,
    extensions: "base",
  });
  if (!searcher) return null;
  const city = await searchOne(searcher, cityAdcode);
  const districts = city ? flattenDistricts(city) : null;
  const out = districts && districts.length > 0 ? districts : null;
  districtsCache.set(cityAdcode, out);
  return out;
}

/** 行政区边界(按需重查询;paths 为高德 boundaries 原样,供 LeadMap 画描边+setBounds)。 */
export async function getBoundary(
  adcode: string
): Promise<{ center: DistrictNode["center"]; paths: any[] } | null> {
  const hit = boundaryCache.get(adcode);
  if (hit !== undefined) return hit;
  const AMap: any = await loadAmap();
  if (!AMap) return null;
  const searcher = await newSearcher(AMap, { subdistrict: 0, extensions: "all" });
  if (!searcher) return null;
  const node = await searchOne(searcher, adcode);
  const out = node
    ? { center: toNode(node).center, paths: (node.boundaries ?? []) as any[] }
    : null;
  boundaryCache.set(adcode, out);
  return out;
}
